import React, { useEffect, useRef, useState } from 'react';
import { FaTimes, FaPaperPlane, FaTrashAlt, FaSpinner } from 'react-icons/fa';
import { IconBaseProps } from 'react-icons';
import './ChatWindow.css';

// 引入 MarkdownRenderer 组件用于渲染 Markdown 内容
import MarkdownRenderer from './MarkdownRenderer';
import eventBus, { EventData } from '../../utils/eventBus';
import { getAiAssistance, showNotification } from '../../services/SaveService';
import { saveChatToLocal, loadChatFromLocal, clearChatLocal } from '../../utils/localStorageHelper';

// 将图标组件转换为React函数组件
const TimesIcon = FaTimes as React.FunctionComponent<IconBaseProps>;
const PaperPlaneIcon = FaPaperPlane as React.FunctionComponent<IconBaseProps>;
const TrashIcon = FaTrashAlt as React.FunctionComponent<IconBaseProps>;
const SpinnerIcon = FaSpinner as React.FunctionComponent<IconBaseProps>;

interface ChatWindowProps {
  visible: boolean;
  messages: { role: 'user' | 'assistant'; content: string }[];
  inputValue: string;
  onInputChange: (v: string) => void;
  onSend: (msg: string) => void;
  onClose: () => void;
  typingSpeed?: number; // 打字速度，毫秒/字符
  useEventBus?: boolean; // 是否使用事件总线
}

// 优化的打字机效果组件，确保与Markdown渲染器样式一致
const TypewriterMarkdown: React.FC<{
  text: string;
  speed: number;
  onComplete: () => void;
}> = ({ text, speed, onComplete }) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayText(text.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      }, speed);
      return () => clearTimeout(timer);
    } else if (currentIndex === text.length && text.length > 0) {
      onComplete();
    }
  }, [currentIndex, text, speed, onComplete]);

  return (
    <div className="typewriter-markdown">
      <MarkdownRenderer content={displayText} />
      {currentIndex < text.length && <span className="cursor">|</span>}
    </div>
  );
};

const ChatWindow: React.FC<ChatWindowProps> = ({
  visible,
  messages,
  inputValue,
  onInputChange,
  onSend,
  onClose,
  typingSpeed = 30, // 默认每字符 30 毫秒
  useEventBus = true, // 默认使用事件总线
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [newResponseIndex, setNewResponseIndex] = useState<number | null>(null);
  const [localMessages, setLocalMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const [localInputValue, setLocalInputValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // 使用本地消息或传入的消息
  const displayMessages = localMessages.length > 0 && useEventBus ? localMessages : messages;
  // 使用本地输入或传入的输入
  const currentInputValue = useEventBus ? localInputValue : inputValue;

  // 加载历史聊天记录（组件初次加载）
  useEffect(() => {
    if (useEventBus) {
      const history = loadChatFromLocal();
      if (history.length > 0) {
        setLocalMessages(history);
        // 历史记录不使用打字效果
        setNewResponseIndex(null);
      }
    }
  }, []);

  // 在 localMessages 更新时自动保存
  useEffect(() => {
    if (useEventBus && localMessages.length > 0) {
      saveChatToLocal(localMessages);
    }
  }, [localMessages, useEventBus]);

  // 滚动到最新消息
  useEffect(() => {
    if (visible && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [displayMessages, visible]);

  // 处理 ESC 键关闭窗口
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && visible) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, onClose]);
  
  // 事件总线监听器设置
  useEffect(() => {
    if (!useEventBus) return;
    
    // 处理代码解释请求
    const handleExplainSnippet = async ({ selection, fullCode, language }: EventData['explainSnippet']) => {
      // 构建用户消息，包含选中的代码和完整代码的信息
      const userMessage = `请解释以下${language || ''}代码的选中部分：
\`\`\`${language || ''}
${selection}
\`\`\``;
      const newMessages = [...localMessages, { role: 'user' as const, content: userMessage }];
      setLocalMessages(newMessages);
       // 设置加载状态
       setIsLoading(true);
      
      try {
        // 使用 SaveService 的 getAiAssistance 函数
        const data = await getAiAssistance(
          userMessage,
          selection,
          fullCode,
          'explain'
        );
        // 添加 AI 回复到消息列表
        const updatedMessages = [...newMessages, { role: 'assistant' as const, content: data.markdown }];
        setLocalMessages(updatedMessages);
        
        // 先关闭加载状态，再设置新响应的索引用于打字效果
        setIsLoading(false);
        setNewResponseIndex(updatedMessages.length - 1);
      } catch (error) {
        // 处理错误
        const errorMessage = `抱歉，解释代码时出现错误：${(error as Error).message}`;
        const errorMessages = [...newMessages, { 
          role: 'assistant' as const, 
          content: errorMessage 
        }];
        setLocalMessages(errorMessages);
        // 先关闭加载状态，再设置错误消息的打字效果
        setIsLoading(false);
        setNewResponseIndex(errorMessages.length - 1);
        // 显示错误通知
        showNotification(errorMessage, 'error');
      }
    };
    

    // 处理 AI 回复事件
    const handleAIAnswer = ({ role, content }: EventData['aiAnswer']) => {
      console.log('收到 aiAnswer 事件:', { role, content });
      // 如果消息来自其他组件，则添加到消息列表
      setLocalMessages(prev => [...prev, { role, content }]);
      
      // 如果是助手回复，设置打字效果
      if (role === 'assistant') {
        setNewResponseIndex(prev => prev === null ? 0 : prev + 1);
      }
    };
    
    // 处理加载状态事件
    const handleSetLoading = ({ isLoading }: EventData['setLoading']) => {
      console.log('收到 setLoading 事件:', { isLoading });
      setIsLoading(isLoading);
    };
    
    // 注册所有事件监听器
    eventBus.on('explainSnippet', handleExplainSnippet);
    eventBus.on('aiAnswer', handleAIAnswer);
    eventBus.on('setLoading', handleSetLoading);
    
    // 清理函数
    return () => {
      eventBus.off('explainSnippet', handleExplainSnippet);
      eventBus.off('aiAnswer', handleAIAnswer);
      eventBus.off('setLoading', handleSetLoading);
    };
  }, [localMessages, useEventBus]);

  // 处理输入框 Enter 键发送消息
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && currentInputValue.trim()) {
      e.preventDefault();
      handleSendMessage(currentInputValue);
    }
  };

  // 处理发送按钮点击
  const handleSendClick = () => {
    if (currentInputValue.trim()) {
      handleSendMessage(currentInputValue);
    }
  };
  
  // 处理发送消息
  const handleSendMessage = async (message: string) => {
    // 如果使用事件总线，则使用本地消息处理
    if (useEventBus) {
      // 添加用户消息到本地消息列表
      const newMessages = [...localMessages, { role: 'user' as const, content: message }];
      setLocalMessages(newMessages);
      
      // 清空输入框
      setLocalInputValue('');
      
      // 设置加载状态
      setIsLoading(true);
      
      try {
        // 请求 AI 回复，使用 SaveService 的 getAiAssistance 函数
        const data = await getAiAssistance(
          message,
          undefined,
          undefined,
          'chat'
        );
        
        // 添加 AI 回复到消息列表
        const updatedMessages = [...newMessages, { role: 'assistant' as const, content: data.markdown }];
        setLocalMessages(updatedMessages);
        // 先关闭加载状态，再设置新响应的索引用于打字效果
        setIsLoading(false);
        setNewResponseIndex(updatedMessages.length - 1);
      } catch (error) {
        // 处理错误
        const errorMessage = `抱歉，无法获取回复：${(error as Error).message}`;
        const errorMessages = [...newMessages, { 
          role: 'assistant' as const, 
          content: errorMessage 
        }];
        setLocalMessages(errorMessages);
        // 先关闭加载状态，再设置错误消息的打字效果
        setIsLoading(false);
        setNewResponseIndex(errorMessages.length - 1);
        // 显示错误通知
        showNotification(errorMessage, 'error');
      }
    } else {
      // 使用传入的 onSend 处理
      onSend(message);
    }
  };

  // 组件不可见时不渲染
  if (!visible) return null;

  // 清空聊天记录
  const handleClearChat = () => {
    setLocalMessages([]);
    clearChatLocal();
  };

  return (
    <div className="chat-window-container">
      <div className="chat-window-header">
        <h3>AI 编程助手</h3>
        <div className="header-buttons">
          {useEventBus && (
            <button className="clear-button" onClick={handleClearChat} title="清空历史">
              <TrashIcon />
            </button>
          )}
          <button className="close-button" onClick={onClose}>
            <TimesIcon />
          </button>
        </div>
      </div>

      <div className="chat-messages">
        {displayMessages.map((message, index) => (
          <div
            key={index}
            className={`message ${message.role === 'user' ? 'user-message' : 'assistant-message'}`}
          >
            <div className="message-sender">
              {message.role === 'user' ? '你' : 'AI'}
            </div>
            <div className="message-content">
              {message.role === 'assistant' ? (
                index === newResponseIndex ? (
                  <TypewriterMarkdown 
                    text={message.content} 
                    speed={typingSpeed}
                    onComplete={() => setNewResponseIndex(null)}
                  />
                ) : (
                  <MarkdownRenderer content={message.content} />
                )
              ) : (
                <div className="user-message-text">{message.content}</div>
              )}
            </div>
          </div>
        ))}
        {isLoading && newResponseIndex === null && (
          <div className="loading-indicator">
            <SpinnerIcon className="spinner" />
            <span>正在思考中...</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className={`chat-input-container ${isInputFocused ? 'focused' : ''}`}>
        <input
          type="text"
          value={currentInputValue}
          onChange={(e) => useEventBus ? setLocalInputValue(e.target.value) : onInputChange(e.target.value)}
          onKeyPress={handleKeyPress}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
          placeholder="输入问题或指令..."
          ref={inputRef}
          className="chat-input"
        />
        <button
          className={`send-button ${!currentInputValue.trim() ? 'disabled' : ''}`}
          onClick={handleSendClick}
          disabled={!currentInputValue.trim()}
        >
          <PaperPlaneIcon />
        </button>
      </div>
    </div>
  );
};

export default ChatWindow;
